import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Planbook } from "@shared/models/planbook"
import type { User } from "@shared/models/user"
import emailPlanbookLinkAction from "cc-frontend/lib/actions/app-state/EMAIL_PLANBOOK_LINK"
import {
  createShareLink,
  describeShareLink,
  perspectiveFromPathname,
} from "cc-frontend/lib/share-link"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type NotifierService from "cc-frontend/services/notifier"
import moment from "cc-frontend/utils/extended-moment"
import EmailInput from "react-app/components/planner/menus/parts/EmailInput"
import styles from "./email-link-form.module.css"

export interface EmailLinkFormSignature {
  Args: {
    closeModal: () => void
    model: {
      // Share link args, passed instead of the share link itself
      // so that we can craft the message.
      pathname: string
      baseUrlParams: URLSearchParams
      /** True whenever navigation is disallowed, including for /lessons links. */
      hideNav: boolean
      dateString: string | null
      courseTitle: string | null

      planbook: Planbook
      user: User
    }
  }
}

export default class EmailLinkForm extends Component<EmailLinkFormSignature> {
  @service declare dispatcher: Dispatcher
  @service declare notifier: NotifierService

  readonly styles = styles
  EmailInput = EmailInput

  @tracked emails: string[] = this.args.model.user.attributes.emailShareSettings?.email || []
  @tracked subject: string = this.emailModalInfo.subject
  @tracked message: string = this.emailModalInfo.message

  get shareLink(): string {
    return createShareLink(this.args.model.pathname, this.args.model.baseUrlParams, {
      hideNav: this.args.model.hideNav,
      dateString: this.args.model.dateString,
      courseTitle: this.args.model.courseTitle,
    })
  }

  get emailModalInfo(): {
    title: string
    subtitle: string
    subject: string
    message: string
    descriptor: string
  } {
    const date = moment(this.args.model.dateString)
    const firstName = this.args.model.user.attributes.firstName
    const defaultSubject = "Lesson plans for"
    const defaultMessage = "Hi!\n\nHere are my lesson plans for"
    const signOff = `\n\nCheers,\n${firstName}`

    if (this.args.model.hideNav) {
      // Links to a specific page and date range.
      const pageDescription = describeShareLink(this.args.model.pathname, {
        dateString: this.args.model.dateString,
        courseTitle: this.args.model.courseTitle,
      })
      switch (perspectiveFromPathname(this.args.model.pathname)) {
        case "day":
          return {
            title: "Email a link to the Day",
            subtitle: pageDescription,
            subject: `${defaultSubject} ${pageDescription} 🎉`,
            message: `${defaultMessage} ${pageDescription}.${signOff}`,
            descriptor: "today",
          }
        case "week": {
          return {
            title: "Email a link to the Week",
            subtitle: pageDescription,
            subject: `${defaultSubject} ${pageDescription}`,
            message: `${defaultMessage} the week of ${pageDescription}.${signOff}`,
            descriptor: "this week",
          }
        }
        case "month":
          return {
            title: "Email a link to the Month",
            subtitle: pageDescription,
            subject: `${defaultSubject} ${pageDescription} 🎉`,
            message: `${defaultMessage} the month of ${date.format("MMMM")}.${signOff}`,
            descriptor: "this month",
          }
        case "timeline":
          return {
            title: "Email a link to the Timeline",
            subtitle: "",
            subject: `Unit Lesson Plans 🎉`,
            message: `Hi!\n\nHere is a link to my Planbook Timeline.${signOff}`,
            descriptor: "the unit timeline",
          }
        case "outline":
          return {
            title: "Email a link to the Planbook Outline",
            subtitle: "",
            subject: `Lesson Plan Outline 🎉`,
            message: `Hi!\n\nHere is a link to my Planbook Outline.${signOff}`,
            descriptor: "the outline view",
          }
        case "standards-tracker":
          return {
            title: "Email a link to the Standards Tracker",
            subtitle: "",
            subject: `Lesson Plan Standard Tracker 🎉`,
            message: `Hi!\n\nHere is a link to my Standards Tracker.${signOff}`,
            descriptor: "the standards tracker",
          }
        case "lessons":
          return {
            title: "Email a link to the lesson",
            subtitle: pageDescription,
            subject: `${defaultSubject} ${pageDescription} 🎉`,
            message: `Hi!\n\nHere is a link to my ${this.args.model
              .courseTitle!} lesson on ${date.format("dddd, MMMM DD, YYYY")}.${signOff}`,
            descriptor: "the lesson",
          }
        default:
          return {
            title: "Email a link to your Planbook",
            subtitle: "",
            subject: `${this.args.model.planbook.attributes.title} Lesson Plans 🎉`,
            message: `Hi!\n\nHere's a link to my planbook.${signOff}`,
            descriptor: "your planbook",
          }
      }
    } else {
      // Links to the whole planbook, though it will start on a specific page.
      return {
        title: "Email a link to your Planbook",
        subtitle: "",
        subject: `${this.args.model.planbook.attributes.title} Lesson Plans 🎉`,
        message: `Hi!\n\nHere's a link to my planbook.${signOff}`,
        descriptor: "your planbook",
      }
    }
  }

  get savedEmails(): string[] {
    return this.args.model.user.attributes.emailShareSettings?.email || []
  }

  @action
  updateEmails(emails: string[]) {
    this.emails = emails
  }

  @action
  updateSubject(subject: string) {
    this.subject = subject
  }

  @action
  updateMessage(message: string) {
    this.message = message
  }

  @action
  sendEmail() {
    if (!this.emails.length) {
      this.notifier.warning("Please enter at least one email address.")
      return
    }
    this.notifier.success("", {
      title: `A link to ${this.emailModalInfo.descriptor} has been sent to ${this.emails.join(
        ", "
      )}!`,
      message: "We're sending them an email now!",
      duration: 5000,
      type: "is-success",
    })

    this.dispatcher.dispatchPreparedAction(
      emailPlanbookLinkAction({
        user: this.args.model.user,
        planbookId: this.args.model.planbook.id,
        planbookTitle: this.args.model.planbook.attributes.title,
        perspective: perspectiveFromPathname(this.args.model.pathname),
        email: this.emails,
        subject: this.subject,
        message: this.message.replace(/\n/g, "<br />"),
        url: this.shareLink,
      })
    )

    this.args.closeModal()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Menus::Parts::EmailLinkForm": typeof EmailLinkForm
  }
}
